import { ChangeEvent, useState } from 'react';
import { NON_ALPHANUMERIC_CHARS } from '@access-cms/shared-constants';

import { DatePicker } from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { required, visaNumber } from '../../utils/fieldValidation';

export interface ImmigrationDocumentFormData {
  immigrationDate: string;
  documentType: string;
  visaNumber: string;
}

function ImmigrationDocumentPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<ImmigrationDocumentFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'immigration-document-page';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'immigrationDate', label: 'Immigration Date', validationRules: [required] },
      { field: 'visaNumber', label: 'Visa Number', validationRules: [required, visaNumber] },
    ],
  });

  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 100);

  const handleChange = (field: keyof ImmigrationDocumentFormData, value: string) => {
    updateData({
      ...data,
      documentType: 'Visa Number',
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  // Format Visa Number: 8 characters - either all numeric or 1 letter + 7 digits
  const handleVisaNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.toUpperCase(); // Visa numbers are typically uppercase
    // Allow alphanumeric characters only
    const cleaned = input.replace(NON_ALPHANUMERIC_CHARS, '');
    // Limit to 8 characters (standard US visa number length)
    const limited = cleaned.slice(0, 8);

    updateData({
      ...data,
      documentType: 'Visa Number',
      visaNumber: limited,
    });
    handleValidationChange('visaNumber', limited);
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-date`}>
        <DatePicker
          required
          data-testid={`${baseTestId}-date-picker`}
          errorMessage={getFieldError('immigrationDate')}
          fromDate={minDate}
          hint='Select the date you immigrated to the United States'
          id='immigrationDate'
          label='Immigration Date'
          name='immigrationDate'
          toDate={today}
          tooltipText='The date you first entered the United States as an immigrant. This is typically the date on your immigration visa or the date you became a permanent resident.'
          value={data.immigrationDate || ''}
          onBlur={() => handleBlur('immigrationDate')}
          onDateChange={(date, formattedDate) => handleChange('immigrationDate', formattedDate || '')}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-document-number`}>
        <TextField
          required
          errorMessage={getFieldError('visaNumber')}
          hint='Format: 8 characters (e.g., 12345678 or A1234567). Found in red ink on the bottom right of the visa stamp.'
          label='Visa Number'
          name='visaNumber'
          tooltipText='An 8-character number printed in red ink on the bottom right corner of your visa document. It consists of either 8 digits or 1 letter followed by 7 digits. This is different from the control number at the top of your visa. Even if your most recent visa stamp has expired, enter the Visa Number from that stamp.'
          type='text'
          value={data.visaNumber || ''}
          onBlur={() => handleBlur('visaNumber')}
          onChange={handleVisaNumberChange}
        />
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Immigration Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Immigration Details</h3>
          <p>
            Please share information from your immigration documents. We will need this data to verify if you are
            allowed to work for the government.
          </p>

          <h4 className='ds-u-margin-top--3'>Immigration Date</h4>
          <p>
            Enter the date you first entered the United States as an immigrant. This may be the date on your immigration
            visa or the date you became a permanent resident.
          </p>

          <h4 className='ds-u-margin-top--3'>Immigration Document Type</h4>
          <ul>
            <li>
              <strong>Visa Number:</strong> An 8-character number printed in red ink on the bottom right corner of your
              visa document. It consists of either 8 digits (e.g., 12345678) or 1 letter followed by 7 digits (e.g.,
              A1234567). This is different from the control number at the top of your visa. Even if your most recent
              visa stamp has expired, enter the Visa Number from that stamp.
            </li>
          </ul>
          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> Make sure to enter your document number as it appears on your official immigration
            documents.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default ImmigrationDocumentPage;
